import { DocumentNode, gql } from '@apollo/client';
import React from 'react';
import {
	DocumentViewEmailNotificationGroupContractForGetContractWhereFragment,
	DocumentViewEmailNotificationGroupInvoiceFragment,
	DocumentViewEmailNotificationGroupFragment,
	UserType,
} from '#types';
import classNames from 'classnames';
import {
	Box,
	Divider,
	List,
	ListItem,
	ListItemText,
	Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { FlattenedUser } from '../../types/user';
import rpcShared from '@rockpapercoin/rpc-shared';
import styles from './DocumentViewEmailNotificationGroup.module.scss';

const documentViewEmailNotificationGroupContractForGetContractWhereFragment = gql`
	fragment documentViewEmailNotificationGroupContractForGetContractWhere on ContractForGetContractWhere {
		id
		emailNotificationGroup {
			id
			groupTitle
			members {
				id
				email
				userType
				orgUser {
					id
					firstName
					lastName
					organization {
						id
					}
				}
			}
		}
	}
`;

const documentViewEmailNotificationGroupInvoiceFragment = gql`
	fragment documentViewEmailNotificationGroupInvoice on Invoice {
		id
		emailNotificationGroup {
			id
			groupTitle
			members {
				id
				email
				userType
				orgUser {
					id
					firstName
					lastName
					organization {
						id
					}
				}
			}
		}
	}
`;

const documentViewEmailNotificationGroupFragment = gql`
	fragment documentViewEmailNotificationGroup on Proposal {
		id
		emailNotificationGroup {
			id
			groupTitle
			members {
				id
				email
				userType
				orgUser {
					id
					firstName
					lastName
					organization {
						id
					}
				}
			}
		}
	}
`;

export type DocumentViewEmailNotificationGroupProps = {
	document?:
	| DocumentViewEmailNotificationGroupContractForGetContractWhereFragment
	| DocumentViewEmailNotificationGroupInvoiceFragment
	| DocumentViewEmailNotificationGroupFragment;
	className?: string;
	isMobile: boolean;
};

type DocumentViewEmailNotificationGroupPropsWithFragment =
	React.FC<DocumentViewEmailNotificationGroupProps> & {
		fragments: {
			documentViewEmailNotificationGroupContractForGetContractWhereFragment: DocumentNode;
			documentViewEmailNotificationGroupInvoiceFragment: DocumentNode;
			documentViewEmailNotificationGroupFragment: DocumentNode;
		};
	};

export const DocumentViewEmailNotificationGroup: DocumentViewEmailNotificationGroupPropsWithFragment =
	( { document, className, isMobile } ) => {
		const user: FlattenedUser = useSelector( ( state: any ) => state.user );
		const organizationId =
			user?.isLoggedIn && user.userType === UserType.OrgUser
				? user.organization.id
				: undefined;
		if ( !document?.emailNotificationGroup ) {
			return null;
		}
		return document?.emailNotificationGroup &&
			document.emailNotificationGroup.members.length > 0 ? (
				<>
					{ isMobile ? <Divider className={ styles.divider } /> : null }
					<Box className={ classNames( className ) }>
						<Typography variant='h6' className={ styles.header }>
							{
								rpcShared.emailNotificationGroups.UserFriendlyGroupNames[
									document.emailNotificationGroup.groupTitle
								]
							}
						</Typography>
						<List disablePadding>
							{ document.emailNotificationGroup.members.map( ( member ) => (
								<ListItem key={ member.id } disablePadding disableGutters>
									<ListItemText
										slotProps={ {
											primary: {
												className: styles.primary,
											},
										} }
										primary={
											member.orgUser &&
										organizationId === member.orgUser.organization?.id
												? `${ member.orgUser.firstName } ${ member.orgUser.lastName }`
												: member.email
										}
									/>
								</ListItem>
							) ) }
						</List>
					</Box>
				</>
			) : null;
	};

DocumentViewEmailNotificationGroup.fragments = {
	documentViewEmailNotificationGroupContractForGetContractWhereFragment,
	documentViewEmailNotificationGroupInvoiceFragment,
	documentViewEmailNotificationGroupFragment,
};
